import { Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { catchError, map, of, switchMap } from 'rxjs';

import { FrontendDiscountHub, FrontendDiscountHubStore } from '../../../core/api/api.models';
import { AssetUrlPipe } from '../../../core/api/asset-url.pipe';
import { SavingArenaApiService } from '../../../core/api/saving-arena-api.service';
import { TrustedCmsHtmlPipe } from '../../../core/api/trusted-cms-html.pipe';
import { SeoService } from '../../../core/seo/seo.service';
import { SsrResponseService } from '../../../core/seo/ssr-response.service';
import { breadcrumbSchemaFor, breadcrumbTrail } from '../../../core/seo/breadcrumb-trail';
import { webPageSchema } from '../../../core/seo/structured-data';
import { Breadcrumb } from '../../../shared/breadcrumb/breadcrumb';

/** A category filter chip, with the number of stores behind it. */
interface CategoryChip {
  slug: string;
  name: string;
  count: number;
}

type SortKey = 'popular' | 'discount' | 'name';

/**
 * `/student-discount` and its siblings — the hub listing every store that has a published page of
 * one discount type.
 *
 * Routed by `discountHubMatcher` (app.routes.ts): the URL is exactly the type's suffix, which is
 * what tells it apart from a single store's `/auxbeam-student-discount`.
 */
@Component({
  selector: 'app-discount-hub-page',
  imports: [Breadcrumb, RouterLink, AssetUrlPipe, TrustedCmsHtmlPipe],
  templateUrl: './discount-hub-page.html',
  styleUrl: './discount-hub-page.scss',
})
export class DiscountHubPage {
  private readonly route = inject(ActivatedRoute);
  private readonly api = inject(SavingArenaApiService);
  private readonly seo = inject(SeoService);
  private readonly ssrResponse = inject(SsrResponseService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly hub = signal<FrontendDiscountHub | null>(null);

  protected readonly crumbs = computed(() => {
    const label = this.hub()?.label || '';
    return breadcrumbTrail(
      { label: 'Stores', path: '/storelist' },
      ...(label ? [{ label }] : []),
    );
  });
  protected readonly isLoading = signal(true);
  protected readonly notFound = computed(() => !this.isLoading() && this.hub() === null);

  protected readonly search = signal('');
  protected readonly activeCategory = signal('');
  protected readonly sortKey = signal<SortKey>('popular');

  protected readonly label = computed(() => this.hub()?.label ?? '');
  /** "Student Discount" -> "student discount", for use mid-sentence. */
  protected readonly labelLower = computed(() => this.label().toLowerCase());

  protected readonly heading = computed(() => {
    const hub = this.hub();
    if (!hub) return '';
    return hub.headingH1 || `Verified ${hub.label.toLowerCase()}s for every store you shop`;
  });

  protected readonly intro = computed(() => {
    const hub = this.hub();
    if (!hub) return '';
    if (hub.intro) return hub.intro;
    return `Every store on SavingArena with a ${hub.label.toLowerCase()} page, with the offers we currently list for it.`;
  });

  protected readonly allStores = computed(() => this.hub()?.stores ?? []);

  /** Latest change across the listed pages — the honest version of "checked today". */
  protected readonly lastUpdated = computed(() => {
    const times = this.allStores()
      .map((store) => (store.updatedAt ? new Date(store.updatedAt).getTime() : 0))
      .filter((time) => time > 0);
    if (!times.length) return '';

    return new Date(Math.max(...times)).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  });

  /** Category chips, built from the listed stores so a chip never leads to an empty result. */
  protected readonly categories = computed<CategoryChip[]>(() => {
    const counts = new Map<string, CategoryChip>();

    for (const store of this.allStores()) {
      for (const category of store.categories) {
        if (!category.slug) continue;
        const existing = counts.get(category.slug);
        if (existing) existing.count += 1;
        else counts.set(category.slug, { slug: category.slug, name: category.name, count: 1 });
      }
    }

    return [...counts.values()].sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  });

  /** Search + category filter + sort, applied in that order. */
  protected readonly visibleStores = computed<FrontendDiscountHubStore[]>(() => {
    const term = this.search().trim().toLowerCase();
    const category = this.activeCategory();

    const filtered = this.allStores().filter((store) => {
      if (category && !store.categories.some((entry) => entry.slug === category)) return false;
      if (!term) return true;

      const haystack = [store.storeName, ...store.categories.map((entry) => entry.name)]
        .join(' ')
        .toLowerCase();
      return haystack.includes(term);
    });

    const sorted = [...filtered];
    const key = this.sortKey();
    if (key === 'name') sorted.sort((a, b) => a.storeName.localeCompare(b.storeName));
    else if (key === 'discount') sorted.sort((a, b) => b.discountPercent - a.discountPercent);
    else sorted.sort((a, b) => b.popularity - a.popularity || b.offerCount - a.offerCount);

    return sorted;
  });

  protected readonly resultLabel = computed(() => {
    const count = this.visibleStores().length;
    return `Showing ${count} store${count === 1 ? '' : 's'}`;
  });

  /** Up to three highlight cards, each backed by a real fact about a real store. */
  protected readonly highlights = computed(() => {
    const stores = this.allStores();
    if (!stores.length) return [];

    const cards: { badge: string; eyebrow: string; title: string; slug: string }[] = [];

    const biggest = [...stores].sort((a, b) => b.discountPercent - a.discountPercent)[0];
    if (biggest && biggest.discountPercent > 0) {
      cards.push({
        badge: `${biggest.discountPercent}%`,
        eyebrow: 'Biggest discount',
        title: biggest.storeName,
        slug: biggest.slug,
      });
    }

    const mostUsed = [...stores].sort((a, b) => b.popularity - a.popularity)[0];
    if (mostUsed && mostUsed.popularity > 0 && mostUsed.slug !== cards[0]?.slug) {
      cards.push({
        badge: mostUsed.storeName.charAt(0).toUpperCase(),
        eyebrow: 'Most used',
        title: mostUsed.storeName,
        slug: mostUsed.slug,
      });
    }

    const newest = [...stores]
      .filter((store) => store.updatedAt)
      .sort((a, b) => new Date(b.updatedAt ?? 0).getTime() - new Date(a.updatedAt ?? 0).getTime())[0];
    if (newest && !cards.some((card) => card.slug === newest.slug)) {
      cards.push({ badge: 'NEW', eyebrow: 'Just added', title: newest.storeName, slug: newest.slug });
    }

    return cards;
  });

  constructor() {
    this.route.paramMap
      .pipe(
        map((params) => (params.get('hubSlug') ?? '').trim()),
        switchMap((suffix) => {
          this.isLoading.set(true);
          this.hub.set(null);
          if (!suffix) return of(null);

          return this.api.getDiscountHub(suffix).pipe(
            map((response) => (response.success ? response.data : null)),
            catchError((error: unknown) => {
              this.ssrResponse.noteFetchFailure(error);
              return of(null);
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((hub) => {
        this.hub.set(hub);
        this.isLoading.set(false);
        this.search.set('');
        this.activeCategory.set('');
        this.sortKey.set('popular');
        this.applySeo();
      });
  }

  protected onSearch(value: string): void {
    this.search.set(value);
  }

  protected selectCategory(slug: string): void {
    this.activeCategory.set(this.activeCategory() === slug ? '' : slug);
  }

  protected setSort(key: SortKey): void {
    this.sortKey.set(key);
  }

  /**
   * The card's offer line and chip come from the assigned coupon's badges — the same three fields
   * the store-page card uses (badge 1 + 2 on top, badge 3 as the chip).
   *
   * Fallbacks only kick in for a slot the admin left blank, so nothing is invented: a parsed
   * percentage first, then the type label, then the coupon's code/deal nature.
   */
  protected offerPrimary(store: FrontendDiscountHubStore): string {
    const badge = store.offerBadges?.[0]?.trim();
    if (badge) return badge;
    if (store.discountPercent > 0) return `${store.discountPercent}%`;
    return this.label();
  }

  protected offerSecondary(store: FrontendDiscountHubStore): string {
    const badge = store.offerBadges?.[1]?.trim();
    if (badge) return badge;
    return store.discountPercent > 0 && !store.offerBadges?.[0]?.trim() ? 'OFF' : '';
  }

  protected offerTypeLabel(store: FrontendDiscountHubStore): string {
    const badge = store.offerBadges?.[2]?.trim();
    if (badge) return badge;
    return store.offerCount > 0 ? store.offerType : '';
  }

  protected offerMeta(store: FrontendDiscountHubStore): string {
    const parts: string[] = [];

    if (store.offerCount > 0) {
      parts.push(`${store.offerCount} offer${store.offerCount === 1 ? '' : 's'}`);
    }

    if (store.endDateText) parts.push(store.endDateText);
    else if (store.endDate) {
      const date = new Date(store.endDate);
      if (!Number.isNaN(date.getTime())) {
        parts.push(`Ends ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`);
      }
    }

    if (store.popularity > 0) parts.push(`${store.popularity.toLocaleString('en-US')} used`);

    return parts.join(' · ');
  }

  private applySeo(): void {
    const hub = this.hub();

    if (!hub) {
      if (this.isLoading()) return;
      this.ssrResponse.setNotFound();
      this.seo.apply({
        title: 'Page not found | SavingArena',
        description: 'This discount hub is not available.',
        noindex: true,
      });
      this.seo.setJsonLd([]);
      return;
    }

    const path = `/${hub.slugSuffix}`;
    const url = this.seo.absolute(path);
    const title = hub.metaTitle || `${hub.label}s: Verified Codes & Deals | SavingArena`;
    const description =
      hub.metaDescription ||
      `Every store with a ${hub.label.toLowerCase()} listed on SavingArena, with the codes and deals we currently track.`;

    this.seo.apply({ title, description, canonicalPath: path });

    this.seo.setJsonLd([
      webPageSchema({ origin: this.seo.origin, url, name: title, description }),
      breadcrumbSchemaFor(this.crumbs(), (p) => this.seo.absolute(p)),
      // An ItemList of the stores listed here, in the order the page shows them by default.
      {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: title,
        numberOfItems: hub.stores.length,
        itemListElement: this.visibleStores().map((store, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: `${store.storeName} ${hub.label}`,
          url: this.seo.absolute(`/${store.slug}`),
        })),
      },
    ]);
  }
}
